import { ApiQueryParams, DefaultQueryParams, PaginatedResponse, fetchAllPages } from '@frontend/api-utils';
import { EquipmentClient } from '@frontend/equipment/api';
import { EquipmentStateName, IEquipment } from '@frontend/equipment/types';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { ProductClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { ScreenTrigger } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useIdentification, useScanners } from '../../../hooks';
import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { submitLendingReturnTransaction } from '../../utils/submit-lending-return-transaction';
import { toError } from '../../utils/to-error';
import { useScreen } from '../useScreen';

export type ReturnEquipmentChoice = 'normal' | 'lost' | 'damaged' | 'tag_broken' | 'incomplete';

interface ViewProps {
    selectedEquipment: IEquipment | null;
    selectedProduct: IProduct | null;
    selectedChoice: ReturnEquipmentChoice;
    isDetailOpen: boolean;
    isVerified: boolean;
    verificationRequired: boolean;
    allowUnidentifiedEquipmentReturn: boolean;
    canMarkIncomplete: boolean;
    isLoadingSelection: boolean;
    isWaitingForEquipmentState: boolean;
    isSubmitting: boolean;
    isBusy: boolean;
    configError: boolean;
    error: string | null;
    // eslint-disable-next-line no-unused-vars
    fetchEquipments: (queryParams?: ApiQueryParams<DefaultQueryParams>) => Promise<PaginatedResponse<IEquipment>>;
    // eslint-disable-next-line no-unused-vars
    onSelectEquipment: (equipment: IEquipment, verified?: boolean) => void;
    onContinueSelection: () => void | Promise<void>;
    onClearSelection: () => void;
    // eslint-disable-next-line no-unused-vars
    setSelectedChoice: (choice: ReturnEquipmentChoice) => void;
    resetError: () => void;
    onBack: () => void;
    onSubmit: () => void;
}

const RETURNABLE_EQUIPMENT_STATES = [EquipmentStateName.EQUIPMENT_IN_USE, EquipmentStateName.EQUIPMENT_LOST];
const EQUIPMENT_STATE_POLL_ATTEMPTS = 10;
const EQUIPMENT_STATE_POLL_DELAY_MS = 500;

const sleep = async (duration: number): Promise<void> => await new Promise((resolve) => setTimeout(resolve, duration));
const requiresVerification = (choice: ReturnEquipmentChoice) => choice === 'normal' || choice === 'damaged';
const isSettingEnabled = (value: string | undefined) => value === 'true';
const isDamagedChoice = (choice: ReturnEquipmentChoice) => choice === 'damaged' || choice === 'tag_broken';
const isIncompleteChoice = (choice: ReturnEquipmentChoice) => choice === 'incomplete';
const isChoiceAllowed = (equipment: IEquipment, choice: ReturnEquipmentChoice) => !(choice === 'lost' && equipment.state === EquipmentStateName.EQUIPMENT_LOST);

export const useReturnEquipmentList = (): ViewProps => {
    const intl = useIntl();
    const location = useLocation();
    const { store, screen_type, navigationState, wizardData, onBack, onSubmit } = useScreen();
    const { userOverwrite } = wizardData;
    const userId = userOverwrite?.id ?? navigationState.user?.id ?? null;
    const locationEquipment = (location.state as { equipment?: IEquipment } | null)?.equipment ?? null;
    const wizardEquipment = wizardData.equipment as IEquipment | undefined;
    const stateEquipment = locationEquipment ?? wizardEquipment ?? null;

    const transactionContext = useWizardTransaction({
        store,
        wizardId: store?.wizard.id,
        autoCreate: false,
        autoFetch: false
    });

    const { setPackages, syncPackages } = useWizardPackages({
        store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        transactionContext
    });

    const { transactionWorkflowId, dropOffPackageWorkflowId, get: getSetting } = useWizardSettings(store);
    const allowUnidentifiedEquipmentReturn = isSettingEnabled(getSetting(SettingKey.ALLOW_UNIDENTIFIED_EQUIPMENT_RETURN));
    const workflowConfigError = !transactionWorkflowId || !dropOffPackageWorkflowId;
    const submitFalseStep = useMemo(
        () => store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT_FALSE),
        [screen_type, store?.steps]
    );

    const [allowedEquipments, setAllowedEquipments] = useState<IEquipment[]>([]);
    const [allowedEquipmentsLoaded, setAllowedEquipmentsLoaded] = useState<boolean>(false);
    const allowedEquipmentRef = useRef<IEquipment[]>([]);
    const [selectedEquipment, setSelectedEquipment] = useState<IEquipment | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
    const [selectedChoice, setSelectedChoice] = useState<ReturnEquipmentChoice>('normal');
    const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
    const [isVerified, setIsVerified] = useState<boolean>(false);
    const [isLoadingSelection, setIsLoadingSelection] = useState<boolean>(false);
    const [isWaitingForEquipmentState, setIsWaitingForEquipmentState] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingScannedEquipment, setPendingScannedEquipment] = useState<IEquipment | null>(null);
    const isSubmittingRef = useRef<boolean>(false);
    const verificationRequired = requiresVerification(selectedChoice) && !allowUnidentifiedEquipmentReturn;
    const canMarkIncomplete = !workflowConfigError;
    const configError = workflowConfigError || (selectedChoice === 'lost' && !submitFalseStep);

    const baseEquipmentQuery = useMemo(
        () => ({
            user_id: userId ?? undefined,
            state: RETURNABLE_EQUIPMENT_STATES
        }),
        [userId]
    );

    const fetchEquipments = useCallback(
        async (queryParams?: ApiQueryParams<DefaultQueryParams>): Promise<PaginatedResponse<IEquipment>> => {
            if (!userId) return { count: 0, results: [] };
            return await EquipmentClient.fetchEquipments({
                ...queryParams,
                ...baseEquipmentQuery
            });
        },
        [baseEquipmentQuery, userId]
    );

    useEffect(() => {
        if (!userId) {
            setAllowedEquipments([]);
            allowedEquipmentRef.current = [];
            setAllowedEquipmentsLoaded(true);
            return;
        }

        let cancelled = false;
        setAllowedEquipmentsLoaded(false);
        fetchAllPages((params) => EquipmentClient.fetchEquipments(params), baseEquipmentQuery)
            .then((equipments) => {
                if (cancelled) return;
                setAllowedEquipments(equipments);
                allowedEquipmentRef.current = equipments;
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.EQUIPMENT.DEFAULT });
            })
            .finally(() => {
                if (!cancelled) setAllowedEquipmentsLoaded(true);
            });

        return () => {
            cancelled = true;
        };
    }, [baseEquipmentQuery, userId]);

    const onSelectEquipment = useCallback(async (equipment: IEquipment, verified = false) => {
        setSelectedEquipment(equipment);
        setSelectedChoice('normal');
        setIsDetailOpen(true);
        setIsVerified(verified);
        setError(null);
        setIsLoadingSelection(true);

        let product: IProduct | null = null;
        try {
            product = await ProductClient.fetchProduct(equipment.account_id, equipment.product_id);
            setSelectedProduct(product);
        } catch (e: unknown) {
            ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.PRODUCT.DEFAULT });
            setSelectedProduct(null);
        } finally {
            setIsLoadingSelection(false);
        }

        setIsDetailOpen(true);
    }, []);

    const handleEquipmentIdentified = useCallback(
        (equipment: IEquipment) => {
            if (!allowedEquipmentsLoaded) {
                setPendingScannedEquipment(equipment);
                setError(null);
                return;
            }

            const match = allowedEquipmentRef.current.find((allowed) => allowed.id === equipment.id);
            if (!match) {
                setError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.return-equipment-list.invalid-scan',
                        defaultMessage: 'Only equipment assigned to the current user can be returned here.',
                        description: 'Error shown when a scanned equipment item is not assigned to the current user during return'
                    })
                );
                return;
            }

            if (selectedEquipment && selectedEquipment.id !== match.id) {
                setError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.return-equipment-list.scan-selected-equipment',
                        defaultMessage: 'Scan the selected equipment to continue.',
                        description: 'Error shown when the user scans a different equipment item than the selected return item'
                    })
                );
                return;
            }

            if (selectedEquipment?.id === match.id) {
                setSelectedEquipment(match);
                setIsVerified(true);
                setError(null);
                return;
            }

            onSelectEquipment(match, true);
        },
        [allowedEquipmentsLoaded, intl, onSelectEquipment, selectedEquipment]
    );

    useEffect(() => {
        if (!stateEquipment || !allowedEquipmentsLoaded) return;
        const match = allowedEquipments.find((equipment) => equipment.id === stateEquipment.id);
        if (match) onSelectEquipment(match, true);
        else {
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.return-equipment-list.invalid-initial-equipment',
                    defaultMessage: 'This equipment cannot be returned by the current user.',
                    description: 'Error shown when preselected equipment cannot be returned by the current user'
                })
            );
        }
    }, [allowedEquipments, allowedEquipmentsLoaded, intl, onSelectEquipment, stateEquipment]);

    useEffect(() => {
        if (!pendingScannedEquipment || !allowedEquipmentsLoaded) return;
        handleEquipmentIdentified(pendingScannedEquipment);
        setPendingScannedEquipment(null);
    }, [allowedEquipmentsLoaded, handleEquipmentIdentified, pendingScannedEquipment]);

    useScanners({
        id: 'RETURN_EQUIPMENT_LIST',
        onDeviceSensorReceived: () => {
            setError(null);
        }
    });

    useIdentification({
        id: 'RETURN_EQUIPMENT_LIST',
        onShowWizard: (_wizardId, _functionalityId, data) => {
            if (data.equipment) handleEquipmentIdentified(data.equipment);
        },
        onNoMatchingIdentificationFound: () => {
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.return-equipment-list.no-match',
                    defaultMessage: 'No matching equipment was found.',
                    description: 'Error shown when a scanned identification does not match any returnable equipment'
                })
            );
        },
        onUnlinkedIdentificationFound: () => {
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.return-equipment-list.unlinked-identification',
                    description: 'Error shown when a scanned code is recognized but not linked to any equipment',
                    defaultMessage: 'This code was recognized but is not linked to any equipment yet. Please try a different code or contact support.'
                })
            );
        },
        onEquipmentIdentified: handleEquipmentIdentified
    });

    const onClearSelection = useCallback(() => {
        setSelectedEquipment(null);
        setSelectedProduct(null);
        setSelectedChoice('normal');
        setIsDetailOpen(false);
        setIsVerified(false);
        setError(null);
    }, []);

    const onContinueSelection = useCallback(async () => {
        if (!selectedEquipment) return;
        setIsDetailOpen(true);
    }, [selectedEquipment]);

    const waitForEquipmentState = useCallback(
        async (equipment: IEquipment, expectedState: EquipmentStateName) => {
            setIsWaitingForEquipmentState(true);
            try {
                for (let attempt = 0; attempt < EQUIPMENT_STATE_POLL_ATTEMPTS; attempt++) {
                    const latestEquipment = await EquipmentClient.fetchEquipment(equipment.account_id, equipment.id);
                    if (latestEquipment.state === expectedState) {
                        setSelectedEquipment(latestEquipment);
                        setAllowedEquipments((prev) => {
                            const next = prev.map((allowed) => (allowed.id === latestEquipment.id ? latestEquipment : allowed));
                            allowedEquipmentRef.current = next;
                            return next;
                        });
                        return latestEquipment;
                    }
                    await sleep(EQUIPMENT_STATE_POLL_DELAY_MS);
                }

                throw new Error(
                    intl.formatMessage({
                        id: 'kiosk.wizard.return-equipment-list.state-timeout',
                        defaultMessage: 'The equipment state was not updated in time. Please try again.',
                        description: 'Error shown when equipment state polling times out after submitting a return condition'
                    })
                );
            } finally {
                setIsWaitingForEquipmentState(false);
            }
        },
        [intl]
    );

    const handleSubmit = useCallback(async () => {
        if (!selectedEquipment || (verificationRequired && !isVerified) || configError || isLoadingSelection || isSubmittingRef.current) return;
        if (!isChoiceAllowed(selectedEquipment, selectedChoice)) return;
        if (!transactionWorkflowId || !dropOffPackageWorkflowId) return;
        if (selectedChoice === 'lost' && !submitFalseStep) return;

        const iot = navigationState.iot;
        if (!iot || !iot.account_id || !iot.spot_id) {
            ErrorHandler.handleError({
                error: new Error('Missing IoT context for return transaction'),
                entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT
            });
            return;
        }

        isSubmittingRef.current = true;
        setIsSubmitting(true);
        setError(null);
        try {
            const equipments = selectedChoice === 'lost' ? [] : [selectedEquipment];

            const result = await submitLendingReturnTransaction({
                iot: { ...iot, spot_id: iot.spot_id },
                equipments,
                userId,
                lostEquipments: selectedChoice === 'lost' ? [selectedEquipment] : [],
                damagedEquipments: isDamagedChoice(selectedChoice) ? [selectedEquipment] : [],
                incompleteEquipments: isIncompleteChoice(selectedChoice) ? [selectedEquipment] : [],
                settings: { transactionWorkflowId, dropOffPackageWorkflowId },
                application: store?.functionality?.application,
                feature: store?.functionality?.feature,
                functionality_id: store?.functionality?.id,
                transactionContext,
                setPackages,
                syncPackages
            });

            if (result.status === 'no-slot') {
                setError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.return-equipment-list.no-slot',
                        defaultMessage: 'No available SLOT could be found for this item.',
                        description: 'Error shown when no SLOT can be found for returning selected equipment'
                    })
                );
                return;
            }

            if (selectedChoice === 'lost') {
                await waitForEquipmentState(selectedEquipment, EquipmentStateName.EQUIPMENT_LOST);
                onSubmit(submitFalseStep);
                return;
            }
            if (isDamagedChoice(selectedChoice)) {
                await waitForEquipmentState(selectedEquipment, EquipmentStateName.EQUIPMENT_BROKEN);
            }

            onSubmit();
        } catch (e: unknown) {
            ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT });
        } finally {
            setIsSubmitting(false);
            isSubmittingRef.current = false;
        }
    }, [
        selectedEquipment,
        verificationRequired,
        isVerified,
        configError,
        isLoadingSelection,
        transactionWorkflowId,
        dropOffPackageWorkflowId,
        navigationState.iot,
        selectedChoice,
        userId,
        transactionContext,
        setPackages,
        syncPackages,
        intl,
        waitForEquipmentState,
        submitFalseStep,
        onSubmit,
        store?.functionality?.application,
        store?.functionality?.feature,
        store?.functionality?.id
    ]);

    return {
        selectedEquipment,
        selectedProduct,
        selectedChoice,
        isDetailOpen,
        isVerified,
        verificationRequired,
        allowUnidentifiedEquipmentReturn,
        canMarkIncomplete,
        isLoadingSelection,
        isWaitingForEquipmentState,
        isSubmitting,
        isBusy: isLoadingSelection || isWaitingForEquipmentState || isSubmitting,
        configError,
        error,
        fetchEquipments,
        onSelectEquipment,
        onContinueSelection,
        onClearSelection,
        setSelectedChoice,
        resetError: () => setError(null),
        onBack,
        onSubmit: handleSubmit
    };
};

export default useReturnEquipmentList;
